import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

const BalanceForecast = ({ forecastData, transactions, confidence }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  // Analyze spending patterns
  const spendingAnalysis = useMemo(() => {
    if (!transactions) return null;

    const expenses = transactions?.filter(t => t?.amount < 0);
    const income = transactions?.filter(t => t?.amount > 0);

    const totalExpenses = expenses?.reduce((sum, t) => sum + Math.abs(t?.amount), 0);
    const totalIncome = income?.reduce((sum, t) => sum + t?.amount, 0);
    const avgDailySpending = totalExpenses / 30;
    const recurringExpenses = expenses?.filter(t => t?.isRecurring);
    const recurringTotal = recurringExpenses?.reduce((sum, t) => sum + Math.abs(t?.amount), 0);

    // Category breakdown
    const categorySpending = {};
    expenses?.forEach(t => {
      const cat = t?.category || 'uncategorized';
      categorySpending[cat] = (categorySpending?.[cat] || 0) + Math.abs(t?.amount);
    });

    const topCategories = Object.entries(categorySpending)?.sort((a, b) => b?.[1] - a?.[1])?.slice(0, 3)?.map(([category, amount]) => ({
        category,
        amount,
        percentage: ((amount / totalExpenses) * 100)?.toFixed(0)
      }));

    return {
      totalExpenses,
      totalIncome,
      avgDailySpending,
      recurringTotal,
      recurringCount: recurringExpenses?.length,
      topCategories,
      savingsRate: totalIncome > 0 ? (((totalIncome - totalExpenses) / totalIncome) * 100)?.toFixed(1) : 0
    };
  }, [transactions]);

  // Enhanced forecast data with confidence intervals
  const enhancedForecastData = useMemo(() => {
    if (!forecastData) return [];

    return forecastData?.map((point, index) => {
      // Calculate confidence interval (wider for future predictions)
      const daysOut = index * 7; // Assuming weekly data points
      const confidenceWidth = (point?.balance * 0.05) * (1 + daysOut / 30); // Increases with time

      return {
        ...point,
        upperBound: point?.balance + confidenceWidth,
        lowerBound: Math.max(0, point?.balance - confidenceWidth),
        confidenceLevel: Math.max(60, confidence - (daysOut / 30) * 10) // Decreases over time
      };
    });
  }, [forecastData, confidence]);

  const currentBalance = enhancedForecastData?.find(d => d?.type === 'actual')?.balance || 0;
  const projectedBalance = enhancedForecastData?.[enhancedForecastData?.length - 1]?.balance;
  const change = projectedBalance - currentBalance;
  const changePercent = ((change / currentBalance) * 100)?.toFixed(1);

  // Identify potential risks
  const risks = useMemo(() => {
    const riskList = [];

    // Low balance warning
    const minProjected = Math.min(...enhancedForecastData?.map(d => d?.lowerBound));
    if (minProjected < 1000) {
      riskList?.push({
        type: 'warning',
        message: 'Your balance may drop below $1,000 in the next 30 days',
        icon: 'AlertTriangle'
      });
    }

    // High spending alert
    if (spendingAnalysis && spendingAnalysis?.avgDailySpending > currentBalance / 30) {
      riskList?.push({
        type: 'error',
        message: 'Current spending rate exceeds sustainable levels',
        icon: 'AlertCircle'
      });
    }

    // Positive savings
    if (spendingAnalysis && parseFloat(spendingAnalysis?.savingsRate) > 20) {
      riskList?.push({
        type: 'success',
        message: `Excellent ${spendingAnalysis?.savingsRate}% savings rate!`,
        icon: 'CheckCircle'
      });
    }

    return riskList;
  }, [enhancedForecastData, spendingAnalysis, currentBalance]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-elevation-3">
          <p className="text-sm font-semibold text-foreground mb-2">{data?.date}</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground">Projected:</span>
              <span className="text-sm font-bold text-primary">
                {formatCurrency(data?.balance)}
              </span>
            </div>
            {data?.type === 'projected' && (
              <>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-xs text-success">Upper:</span>
                  <span className="text-xs font-semibold text-success">
                    {formatCurrency(data?.upperBound)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-xs text-error">Lower:</span>
                  <span className="text-xs font-semibold text-error">
                    {formatCurrency(data?.lowerBound)}
                  </span>
                </div>
                <div className="mt-2 pt-2 border-t border-border">
                  <span className="text-xs text-muted-foreground">
                    Confidence: {data?.confidenceLevel?.toFixed(0)}%
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-elevation-2">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">
            30-Day Balance Forecast
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            AI-powered prediction with confidence intervals
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg">
          <Icon name="Target" size={16} className="text-primary" />
          <span className="text-xs md:text-sm font-semibold text-foreground">
            {confidence}% confidence
          </span>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
        <div className="p-3 md:p-4 bg-muted rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="DollarSign" size={16} className="text-muted-foreground" />
            <span className="text-xs md:text-sm font-medium text-muted-foreground">
              Current Balance
            </span>
          </div>
          <p className="text-lg md:text-xl font-bold text-foreground">
            {formatCurrency(currentBalance)}
          </p>
        </div>

        <div className="p-3 md:p-4 bg-primary/10 border border-primary/20 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" size={16} className="text-primary" />
            <span className="text-xs md:text-sm font-medium text-muted-foreground">
              Projected (30d)
            </span>
          </div>
          <p className="text-lg md:text-xl font-bold text-foreground">
            {formatCurrency(projectedBalance)}
          </p>
        </div>

        <div className={`p-3 md:p-4 rounded-xl ${
          change >= 0 ? 'bg-success/10 border border-success/20' : 'bg-error/10 border border-error/20'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <Icon name={change >= 0 ? 'ArrowUp' : 'ArrowDown'} size={16} className={change >= 0 ? 'text-success' : 'text-error'} />
            <span className="text-xs md:text-sm font-medium text-muted-foreground">
              Change
            </span>
          </div>
          <p className={`text-lg md:text-xl font-bold ${change >= 0 ? 'text-success' : 'text-error'}`}>
            {change >= 0 ? '+' : ''}{formatCurrency(change)}
            <span className="text-sm ml-2">({change >= 0 ? '+' : ''}{changePercent}%)</span>
          </p>
        </div>

        <div className="p-3 md:p-4 bg-muted rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Activity" size={16} className="text-muted-foreground" />
            <span className="text-xs md:text-sm font-medium text-muted-foreground">
              Avg Daily Spend
            </span>
          </div>
          <p className="text-lg md:text-xl font-bold text-foreground">
            {formatCurrency(spendingAnalysis?.avgDailySpending || 0)}
          </p>
        </div>
      </div>

      {/* Forecast Chart with Confidence Intervals */}
      <div className="h-56 md:h-72 w-full mb-4 md:mb-6" aria-label="Balance Forecast Chart with Confidence Intervals">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={enhancedForecastData}>
            <defs>
              <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorConfidence" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.1}/>
                <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="date" 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine 
              y={currentBalance} 
              stroke="var(--color-muted-foreground)" 
              strokeDasharray="3 3" 
              label={{ value: 'Current', position: 'insideTopRight', fill: 'var(--color-muted-foreground)', fontSize: 12 }}
            />
            {/* Confidence interval area */}
            <Area 
              type="monotone" 
              dataKey="upperBound" 
              stroke="none"
              fill="url(#colorConfidence)"
              fillOpacity={0.3}
            />
            <Area 
              type="monotone" 
              dataKey="lowerBound" 
              stroke="none"
              fill="url(#colorConfidence)"
              fillOpacity={0.3}
            />
            {/* Main forecast line */}
            <Area 
              type="monotone" 
              dataKey="balance" 
              stroke="var(--color-primary)" 
              strokeWidth={3}
              fill="url(#colorBalance)"
              dot={(props) => {
                if (props?.payload?.type === 'actual') {
                  return <circle cx={props?.cx} cy={props?.cy} r={4} fill="var(--color-primary)" />;
                }
                return null;
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Spending Pattern Analysis */}
      {spendingAnalysis && (
        <div className="mb-4 md:mb-6 p-4 bg-muted rounded-xl">
          <h4 className="text-sm md:text-base font-semibold text-foreground mb-3">
            Spending Pattern Analysis
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Total Expenses (30d):</span>
                <span className="text-sm font-bold text-error">
                  {formatCurrency(spendingAnalysis?.totalExpenses)}
                </span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Total Income (30d):</span>
                <span className="text-sm font-bold text-success">
                  {formatCurrency(spendingAnalysis?.totalIncome)}
                </span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Recurring Expenses:</span>
                <span className="text-sm font-bold text-foreground">
                  {formatCurrency(spendingAnalysis?.recurringTotal)} ({spendingAnalysis?.recurringCount})
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Savings Rate:</span>
                <span className={`text-sm font-bold ${
                  parseFloat(spendingAnalysis?.savingsRate) > 20 ? 'text-success' : 
                  parseFloat(spendingAnalysis?.savingsRate) > 10 ? 'text-warning' : 'text-error'
                }`}>
                  {spendingAnalysis?.savingsRate}%
                </span>
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-2">Top Spending Categories:</p>
              {spendingAnalysis?.topCategories?.map((cat, idx) => (
                <div key={idx} className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-foreground capitalize">
                    {idx + 1}. {cat?.category}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatCurrency(cat?.amount)} ({cat?.percentage}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Risks and Alerts */}
      {risks?.length > 0 && (
        <div className="space-y-3 mb-4 md:mb-6">
          {risks?.map((risk, idx) => (
            <div 
              key={idx}
              className={`flex items-start gap-3 p-3 md:p-4 rounded-xl ${
                risk?.type === 'success' ? 'bg-success/10 border border-success/20' :
                risk?.type === 'warning'? 'bg-warning/10 border border-warning/20' : 'bg-error/10 border border-error/20'
              }`}
            >
              <Icon 
                name={risk?.icon} 
                size={20} 
                className={`flex-shrink-0 mt-0.5 ${
                  risk?.type === 'success' ? 'text-success' :
                  risk?.type === 'warning' ? 'text-warning' : 'text-error'
                }`} 
              />
              <p className="text-xs md:text-sm text-foreground">{risk?.message}</p>
            </div>
          ))}
        </div>
      )}

      {/* Methodology */}
      <div className="space-y-3">
        <div className="flex items-start gap-3 p-3 md:p-4 bg-muted rounded-xl">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm md:text-base font-semibold text-foreground mb-1">
              Forecast Methodology
            </h4>
            <p className="text-xs md:text-sm text-muted-foreground">
              This 30-day projection uses advanced machine learning algorithms to analyze your historical spending patterns, 
              recurring transactions, seasonal trends, and income cycles. The confidence intervals (shaded area) represent 
              the range where your actual balance is likely to fall, with {confidence}% statistical confidence. 
              Confidence decreases further into the future as uncertainty increases.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-3 md:p-4 bg-warning/10 border border-warning/20 rounded-xl">
          <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm md:text-base font-semibold text-foreground mb-1">
              Important Disclaimer
            </h4>
            <p className="text-xs md:text-sm text-muted-foreground">
              Forecasts are statistical estimates based on past behavior and cannot predict unexpected expenses, 
              income changes, or life events. Use this as a planning tool, but maintain an emergency fund and 
              review your budget regularly. Actual results may vary significantly from projections.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BalanceForecast;